import {
  ArrowRight,
  BookOpenCheck,
  Building2,
  CalendarCheck2,
  CheckCircle2,
  GraduationCap,
  Languages,
  Lock,
  Mail,
  Moon,
  Phone,
  School,
  Smartphone,
  Sun,
  UserRound,
  UsersRound,
} from 'lucide-react'
import { useEffect, useState, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { LanguagePicker } from '@/features/preferences/components/PreferenceControls'
import { usePreferences } from '@/features/preferences/store'
import { SCHOOL_MODULES, SCHOOL_MODULE_META } from '@/features/school/modules'
import { schoolAddress, type SiteContext } from '@/features/site/context'
import { APP_NAME } from '@/utils/brand'

const CORE = [
  {
    icon: GraduationCap,
    title: 'Students & admissions',
    text: 'Admissions, guardians, enrolment, promotions and documents in one record.',
  },
  {
    icon: UsersRound,
    title: 'Staff',
    text: 'Directory, departments, roles and logins for every member of staff.',
  },
  {
    icon: School,
    title: 'Classes & subjects',
    text: 'Academic years, classes, sections, curricula and class teachers.',
  },
] as const

const PORTALS = [
  {
    icon: Building2,
    title: 'Administration',
    text: 'The school office runs admissions, fees, exams and timetables, with a live dashboard.',
  },
  {
    icon: BookOpenCheck,
    title: 'Teachers & staff',
    text: 'Registers, marks, lessons and homework, their timetable and cover — from any device.',
  },
  {
    icon: UserRound,
    title: 'Students & families',
    text: 'Attendance, results, fees, homework and notices, with absence requests online.',
  },
] as const

const STRENGTHS = [
  { icon: Building2, text: 'Each school gets its own address and data, fully separated.' },
  { icon: Lock, text: 'Secure sign-in, roles and permissions for every portal.' },
  { icon: Languages, text: 'French and English, switchable at any time.' },
  { icon: Smartphone, text: 'Works on phones, tablets and computers.' },
  { icon: Moon, text: 'Light and dark themes for comfortable reading.' },
  { icon: CalendarCheck2, text: 'Modules chosen per school: use only what you need.' },
] as const

/** The public SchoolEduca website (base domain and www.). */
export function WebsitePage({ context }: { context: SiteContext | null }) {
  const { t } = useTranslation('site')
  const { theme, setTheme } = usePreferences()
  const [school, setSchool] = useState('')
  const [invalid, setInvalid] = useState(false)
  const year = new Date().getFullYear()
  const contact = context?.contactEmail
  const phone = context?.contactPhone ?? null
  // tel: keeps only the digits and a leading + ("038 73 532 76" → tel:0387353276).
  const phoneHref = phone ? `tel:${phone.replace(/(?!^\+)[^0-9]/g, '')}` : null

  useEffect(() => {
    document.title = `${APP_NAME} — ${t('School management platform')}`
  }, [t])

  const findSchool = (e: SyntheticEvent) => {
    e.preventDefault()
    const sub =
      school
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .split('.')[0] ?? ''
    const url = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/.test(sub) && context ? schoolAddress(context, sub) : null
    if (!url) {
      setInvalid(true)
      return
    }
    window.location.href = `${url}/login`
  }

  const demoHref = contact
    ? `mailto:${contact}?subject=${encodeURIComponent(t('Demo request — {{name}}', { name: APP_NAME }))}`
    : '#find'

  return (
    <div className="min-h-dvh bg-canvas text-ink">
      <header className="sticky top-0 z-30 border-b border-line/70 bg-surface/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-6">
          <a href="#top" className="flex items-center gap-2.5">
            <span className="bg-brand-gradient grid size-9 place-items-center rounded-xl text-white shadow-card">
              <GraduationCap className="size-5" aria-hidden="true" />
            </span>
            <span className="text-[0.9375rem] font-semibold">{APP_NAME}</span>
          </a>
          <nav
            aria-label={t('Sections')}
            className="ml-6 hidden items-center gap-6 text-sm text-ink-soft md:flex"
          >
            <a href="#features" className="hover:text-ink">
              {t('Features')}
            </a>
            <a href="#portals" className="hover:text-ink">
              {t('Portals')}
            </a>
            <a href="#why" className="hover:text-ink">
              {t('Why us')}
            </a>
            <a href="#contact" className="hover:text-ink">
              {t('Contact')}
            </a>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <LanguagePicker compact />
            <button
              type="button"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="grid size-9 place-items-center rounded-control text-ink-soft hover:bg-surface-sunken hover:text-ink"
              aria-label={theme === 'dark' ? t('Light theme') : t('Dark theme')}
            >
              {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
            <Button asChild size="sm" className="hidden sm:inline-flex">
              <a href="#find">{t('Sign in to my school')}</a>
            </Button>
          </div>
        </div>
      </header>

      <main id="top">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-[40rem] bg-gradient-to-b from-brand-100/70 via-brand-50/40 to-transparent"
          />
          <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-200/70">
                <CheckCircle2 className="size-3.5" aria-hidden="true" />
                {t('For schools of every size')}
              </p>
              <h1 className="mt-5 text-4xl leading-[1.1] font-semibold tracking-tight sm:text-5xl">
                {t('Everything your school runs on, in one calm place.')}
              </h1>
              <p className="mt-5 max-w-xl text-lg text-ink-soft">
                {t(
                  'Admissions, attendance, timetables, exams, fees, online learning and family portals — one platform for the office, teachers, students and parents.',
                )}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" rightIcon={<ArrowRight />}>
                  <a href={demoHref}>{t('Request a demo')}</a>
                </Button>
                <Button asChild size="lg" variant="secondary">
                  <a href="#find">{t('Find my school')}</a>
                </Button>
              </div>
            </div>
            <HeroPreview />
          </div>
        </section>

        {/* Features */}
        <section id="features" className="scroll-mt-20 border-t border-line bg-surface py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <SectionTitle
              eyebrow={t('Features')}
              title={t('One platform, every part of school life')}
              text={t('Start with the essentials and turn on the modules your school needs.')}
            />
            <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {CORE.map(({ icon: Icon, title, text }) => (
                <FeatureCard key={title} icon={<Icon />} title={t(title)} text={t(text)} />
              ))}
              {SCHOOL_MODULES.map((key) => {
                const meta = SCHOOL_MODULE_META[key]
                const Icon = meta.icon
                return (
                  <FeatureCard key={key} icon={<Icon />} title={t(meta.label)} text={t(meta.description)} />
                )
              })}
            </ul>
          </div>
        </section>

        {/* Portals */}
        <section id="portals" className="scroll-mt-20 py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <SectionTitle
              eyebrow={t('Portals')}
              title={t('A space for everyone in the school')}
              text={t(
                'Each person sees what matters to them, in their language, on their phone or computer.',
              )}
            />
            <ul className="mt-12 grid gap-4 md:grid-cols-3">
              {PORTALS.map(({ icon: Icon, title, text }) => (
                <li key={title} className="rounded-card border border-line bg-surface p-6 shadow-card">
                  <span className="bg-brand-gradient grid size-11 place-items-center rounded-xl text-white shadow-card">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold">{t(title)}</h3>
                  <p className="mt-2 text-sm text-ink-soft">{t(text)}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Why */}
        <section id="why" className="scroll-mt-20 border-y border-line bg-surface py-20">
          <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-2">
            <SectionTitle
              align="left"
              eyebrow={t('Why us')}
              title={t('Built for schools, ready in days')}
              text={t(
                'We create your school’s space, you invite your team, and a guide takes you through the set-up step by step.',
              )}
            />
            <ul className="grid gap-3 sm:grid-cols-2">
              {STRENGTHS.map(({ icon: Icon, text }) => (
                <li
                  key={text}
                  className="flex items-start gap-3 rounded-control border border-line bg-canvas p-4"
                >
                  <Icon className="mt-0.5 size-5 shrink-0 text-brand-600" aria-hidden="true" />
                  <span className="text-sm text-ink-soft">{t(text)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Find my school */}
        <section id="find" className="scroll-mt-20 py-20">
          <div className="mx-auto max-w-xl px-4 text-center sm:px-6">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t('Sign in to your school')}
            </h2>
            <p className="mt-3 text-ink-soft">
              {t('Each school has its own address. Enter the name your school gave you.')}
            </p>
            <form onSubmit={findSchool} className="mt-8 flex flex-col gap-3 sm:flex-row" noValidate>
              <div className="flex flex-1 items-center overflow-hidden rounded-control border border-line bg-surface focus-within:ring-2 focus-within:ring-brand-500">
                <Input
                  aria-label={t('Your school’s address')}
                  aria-invalid={invalid}
                  placeholder={t('myschool')}
                  value={school}
                  onChange={(e) => {
                    setSchool(e.target.value)
                    setInvalid(false)
                  }}
                  className="border-0 shadow-none focus-visible:ring-0"
                />
                {context?.baseDomain ? (
                  <span className="shrink-0 pr-3 text-sm text-muted">.{context.baseDomain}</span>
                ) : null}
              </div>
              <Button type="submit" size="lg" rightIcon={<ArrowRight />}>
                {t('Continue')}
              </Button>
            </form>
            {invalid ? (
              <p className="mt-2 text-sm text-danger-700">
                {t('Use the letters, digits and hyphens of your school’s address.')}
              </p>
            ) : null}
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="scroll-mt-20 px-4 pb-20 sm:px-6">
          <div className="bg-brand-gradient mx-auto max-w-6xl overflow-hidden rounded-card px-6 py-12 text-center text-white shadow-raised sm:px-12">
            <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              {t('Ready to bring your school online?')}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-white/85">
              {t('Tell us about your school; we will set up your space and show you around.')}
            </p>
            {contact || phoneHref ? (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                {contact ? (
                  <Button
                    asChild
                    size="lg"
                    variant="secondary"
                    className="h-auto min-h-11 max-w-full py-2.5 break-all whitespace-normal"
                  >
                    {/* asChild renders the link as is: the icon goes inside it. */}
                    <a href={demoHref}>
                      <Mail aria-hidden="true" />
                      {contact}
                    </a>
                  </Button>
                ) : null}
                {phone && phoneHref ? (
                  <Button asChild size="lg" variant="secondary" className="h-auto min-h-11 py-2.5">
                    <a href={phoneHref} aria-label={t('Call {{phone}}', { phone })}>
                      <Phone aria-hidden="true" />
                      {phone}
                    </a>
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      </main>

      <footer className="border-t border-line bg-surface">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm text-muted sm:flex-row sm:px-6">
          <p>
            © {year} {APP_NAME}
          </p>
          {contact || phone ? (
            <p className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
              {contact ? (
                <a href={`mailto:${contact}`} className="inline-flex items-center gap-1.5 hover:text-ink">
                  <Mail className="size-4" aria-hidden="true" />
                  {contact}
                </a>
              ) : null}
              {phone && phoneHref ? (
                <a href={phoneHref} className="inline-flex items-center gap-1.5 hover:text-ink">
                  <Phone className="size-4" aria-hidden="true" />
                  {phone}
                </a>
              ) : null}
            </p>
          ) : null}
        </div>
      </footer>
    </div>
  )
}

function SectionTitle({
  eyebrow,
  title,
  text,
  align = 'center',
}: {
  eyebrow: string
  title: string
  text: string
  align?: 'center' | 'left'
}) {
  return (
    <div className={align === 'center' ? 'mx-auto max-w-2xl text-center' : ''}>
      <p className="text-sm font-semibold tracking-wide text-brand-600 uppercase">{eyebrow}</p>
      <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
      <p className="mt-4 text-ink-soft">{text}</p>
    </div>
  )
}

function FeatureCard({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <li className="rounded-card border border-line bg-canvas p-5 transition hover:border-brand-300 hover:shadow-card">
      <span className="grid size-10 place-items-center rounded-control bg-brand-50 text-brand-600 [&_svg]:size-5">
        {icon}
      </span>
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-1.5 text-sm text-ink-soft">{text}</p>
    </li>
  )
}

/** A stylised glimpse of the dashboard, drawn with the design system (no screenshot to maintain). */
function HeroPreview() {
  const { t } = useTranslation('site')
  const bars = [62, 80, 55, 90, 72, 84, 68]
  return (
    <div aria-hidden="true" className="relative mx-auto w-full max-w-lg">
      <div className="bg-brand-gradient absolute -inset-4 rounded-[2rem] opacity-20 blur-2xl" />
      <div className="relative rounded-card border border-line bg-surface p-5 shadow-overlay">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">{t('Dashboard')}</p>
          <span className="rounded-full bg-success-50 px-2 py-0.5 text-2xs font-semibold text-success-700">
            {t('Live')}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[
            [t('Students'), '1 248'],
            [t('Present today'), '96 %'],
            [t('Fees collected'), '87 %'],
          ].map(([label, value]) => (
            <div key={label} className="rounded-control border border-line bg-canvas p-3">
              <p className="text-2xs text-muted uppercase">{label}</p>
              <p className="mt-1 text-lg font-semibold tabular">{value}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-control border border-line bg-canvas p-3">
          <p className="text-2xs text-muted uppercase">{t('Attendance this week')}</p>
          <div className="mt-3 flex h-24 items-end gap-2">
            {bars.map((h, i) => (
              <div key={i} className="flex-1 rounded-t bg-brand-500/80" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {[
            t('Mathematics — homework due Friday'),
            t('Parents’ meeting on Thursday'),
            t('Grade 6 timetable updated'),
          ].map((line) => (
            <div
              key={line}
              className="flex items-center gap-2 rounded-control bg-canvas px-3 py-2 text-xs text-ink-soft"
            >
              <span className="size-1.5 rounded-full bg-brand-500" />
              {line}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
